import React, {
  FC,
  startTransition,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import {useTranslation} from 'react-i18next'
import {RouteComponentProps, useLocation} from 'react-router'
import {Redirect, Route, Switch, useHistory} from 'react-router-dom'

import useChangeLanguage from '@progyny/components/Navigation/useChangeLanguage'
import PageLoader from '@progyny/components/PageLoader'
import {useMeQuery} from '@progyny/generated/graphql'
import {withContainer} from '@progyny/hocs/ComponentsContainer'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {useScript} from '@progyny/hooks/useScript'
import NotFoundPage from '@progyny/pages/404'
import AprylNavigatorRoutes from '@progyny/pages/AprylNavigator'
import BlockedAccount from '@progyny/pages/BlockedAccount'
import PartnerAgreement from '@progyny/pages/Budget/PartnerAgreement'
import StringOptions from '@progyny/pages/Budget/Wallet/StringOptions'
import ChangeJourney from '@progyny/pages/ChangeJourney'
import ConfirmEmail from '@progyny/pages/ConfirmEmail'
import Consultations from '@progyny/pages/Consultations'
import CreateAccount from '@progyny/pages/CreateAccount'
import PersonalizeJourney from '@progyny/pages/CreateAccount/PersonalizeJourney'
import SelectJourney from '@progyny/pages/CreateAccount/SelectJourney'
import MemberAspiring from '@progyny/pages/Home/MemberAspiring'
import MemberPromotion from '@progyny/pages/Home/MemberPromotion'
import KnowledgeCenterRoutes from '@progyny/pages/KnowledgeCenter'
import Article from '@progyny/pages/KnowledgeCenter/Article'
import Logout from '@progyny/pages/Logout'
import MarketplaceRoutes from '@progyny/pages/Marketplace'
import MigrateAccount from '@progyny/pages/MigrateAccount'
import AcceptedAgreement from '@progyny/pages/PartnersAgreement/AcceptedAgreement'
import PartnerAgreementErrorPage from '@progyny/pages/PartnersAgreement/PartnerAgreementError'
import RejectedAgreement from '@progyny/pages/PartnersAgreement/RejectedAgreement'
import PasswordTokenError from '@progyny/pages/PasswordTokenError'
import ForgetPassword from '@progyny/pages/RecoverPassword'
import CompleteRegistration from '@progyny/pages/Register/Complete'
import RegistrationEmailRequested from '@progyny/pages/Register/EmailRequested'
import RegistrationISN from '@progyny/pages/Register/ISN'
import RequestRegistrationEmail from '@progyny/pages/Register/RequestEmail'
import RegistrationWWT from '@progyny/pages/Register/WWT'
import ResetPassword from '@progyny/pages/ResetPassword'
import SetUpProfile from '@progyny/pages/SetUpProfile'
import Signup from '@progyny/pages/Signup'
import TokenExpired from '@progyny/pages/TokenExpired'
import ValidateToken from '@progyny/pages/ValidateEmail'
import ValidatePrivateEmail from '@progyny/pages/ValidatePrivateEmail'
import CannotSignUpPage from '@progyny/pages/cannot-sign-up'
import ErrorPage from '@progyny/pages/error'
import Login from '@progyny/pages/login'
import {AppRoute} from '@progyny/routes/AppRoute'
import BenefitsGuideRoute from '@progyny/routes/components/BenefitsGuide'
import BudgetRoutes from '@progyny/routes/components/budget'
import ProfileRoutes from '@progyny/routes/components/profile'
import {useMeStore} from '@progyny/store/meStore'
import {
  defaultNavigationState,
  initialNavigationState,
  logoNavigationState,
  type NavigationState as withNavigation,
} from '@progyny/store/navigationStore'
import {
  FERTILITY,
  MENOPAUSE,
  PREGNANCY_AND_POSTPARTUM,
} from '@progyny/utils/constants'
import {
  REACT_APP_BACKEND_URL,
  REACT_APP_HEAP_ANALYTICS_ID,
} from '@progyny/utils/env'
import {isDevelopment} from '@progyny/utils/env'
import {getResumeRoute} from '@progyny/utils/getOnboardingResumePath'

const Home = React.lazy(() => import('@progyny/pages/Home'))
const ServiceProvider = React.lazy(
  () => import('@progyny/pages/ServiceProvider')
)

export enum RouteEnum {
  ACCEPTED_AGREEMENT = 'ACCEPTED_AGREEMENT',
  APRYL_NAVIGATOR = 'APRYL_NAVIGATOR',
  ARTICLES = 'ARTICLES',
  ASPIRING_INFO = 'ASPIRING_INFO',
  BENEFITS_GUIDE_REDIRECT = 'BENEFITS_GUIDE_REDIRECT',
  BLOCKED_ACCOUNT = 'BLOCKED_ACCOUNT',
  BUDGET = 'BUDGET',
  CANNOT_SIGNUP = 'CANNOT_SIGNUP',
  CHANGE_JOURNEY = 'CHANGE_JOURNEY',
  CONFIRM_EMAIL = 'CONFIRM_EMAIL',
  CONSULTATIONS = 'CONSULTATIONS',
  CREATE_ACCOUNT = 'CREATE_ACCOUNT',
  ERROR_AGREEMENT = 'ERROR_AGREEMENT',
  EMAIL_TOKEN_EXPIRED = 'EMAIL_TOKEN_EXPIRED',
  EN_REDIRECT = 'EN_REDIRECT',
  ERROR = 'ERROR',
  FORGET_PASSWORD = 'FORGET_PASSWORD',
  HOME = 'HOME',
  KNOWLEDGE_CENTER = 'KNOWLEDGE_CENTER',
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  MARKETPLACE = 'MARKETPLACE',
  MEMBER_PROMOTION = 'MEMBER_PROMOTION',
  MIGRATE_ACCOUNT = 'MIGRATE_ACCOUNT',
  NON_EXISTENT = 'NON_EXISTENT',
  NOT_FOUND = 'NOT_FOUND',
  PASSWORD_TOKEN_ERROR = 'PASSWORD_TOKEN_ERROR',
  PA_REDIRECT = 'PA_REDIRECT',
  PROFILE = 'PROFILE',
  REGISTER_COMPLETE = 'REGISTER_COMPLETE',
  REGISTER_EMAIL_REQUESTED = 'REGISTER_EMAIL_REQUESTED',
  REGISTER_REQUEST_EMAIL = 'REGISTER_REQUEST_EMAIL',
  REGISTER_ISN = 'REGISTER_ISN',
  REGISTER_WWT = 'REGISTER_WWT',
  RESET_PASSWORD = 'RESET_PASSWORD',
  SERVICE_PROVIDERS = 'SERVICE_PROVIDERS',
  SETUP_PROFILE = 'SETUP_PROFILE',
  SELECT_JOURNEY = 'SELECT_JOURNEY',
  PERSONALIZE_JOURNEY = 'PERSONALIZE_JOURNEY',
  SIGNUP = 'SIGNUP',
  REJECTED_AGREEMENT = 'REJECTED_AGREEMENT',
  VALIDATE_PRIVATE_EMAIL = 'VALIDATE_PRIVATE_EMAIL',
  VALIDATE_TOKEN = 'VALIDATE_TOKEN',
  STRING_OPTIONS = 'STRING_OPTIONS',
}

export const useQuery = () => {
  return new URLSearchParams(useLocation().search)
}

export type RouteSpec = {
  title: string
  path: string
  component?: React.ComponentType<any>
  render?: (props: RouteComponentProps<any>) => React.ReactNode
  withNavigation?: withNavigation
  onlyAuthorized?: boolean
  notExact?: boolean
  withBudget?: boolean
}

type RouteMap = Record<RouteEnum, RouteSpec>

type NavigationMenu = RouteEnum[]
type NavigationMenuItem = {
  path: string
  title: string
}

type useRoutesHookReturnType = {
  routes: RouteMap
  navbarItems: NavigationMenuItem[]
  getPath: (route: RouteEnum) => string
  goTo: (route: RouteEnum) => void
}

// Define navigation menus for different product lines
const getNavigationMenu = (productLine: string): NavigationMenu => {
  const baseMenu = [
    RouteEnum.HOME,
    RouteEnum.KNOWLEDGE_CENTER,
    RouteEnum.APRYL_NAVIGATOR,
    RouteEnum.CONSULTATIONS,
  ]

  switch (productLine) {
    case PREGNANCY_AND_POSTPARTUM:
      // Pregnancy: all except budget and service providers
      return baseMenu
    case MENOPAUSE:
      // Menopause: all except budget
      return [...baseMenu, RouteEnum.SERVICE_PROVIDERS]
    case FERTILITY:
    default:
      // Fertility (default): all items
      return [...baseMenu, RouteEnum.SERVICE_PROVIDERS, RouteEnum.BUDGET]
  }
}

const NO_TITLE = ''

export function useRoutes(
  hasBudget?: boolean,
  loadingMeQuery?: boolean,
  contractType?: string,
  productLine?: string
): useRoutesHookReturnType {
  const {t} = useTranslation()
  const history = useHistory()

  const routes: RouteMap = useMemo(
    () => ({
      [RouteEnum.HOME]: {
        title: t('routes_nav_home'),
        path: '/home',
        component: withContainer(Home),
        onlyAuthorized: true,
        withNavigation: defaultNavigationState,
      },
      [RouteEnum.KNOWLEDGE_CENTER]: {
        title: t('routes_nav_knowledge_center'),
        path: '/knowledge-center',
        component: KnowledgeCenterRoutes,
        onlyAuthorized: true,
        notExact: true,
        withNavigation: defaultNavigationState,
      },
      [RouteEnum.APRYL_NAVIGATOR]: {
        title: t('routes_nav_navigator'),
        path: '/navigator',
        component: withContainer(AprylNavigatorRoutes),
        onlyAuthorized: true,
        withNavigation: {...defaultNavigationState},
      },
      [RouteEnum.ARTICLES]: {
        title: t('routes_nav_article'),
        path: '/articles',
        component: withContainer(Article),
        onlyAuthorized: true,
        notExact: true,
        withNavigation: defaultNavigationState,
      },
      [RouteEnum.CONSULTATIONS]: {
        title: t('routes_nav_consultations'),
        path: '/consultations',
        component: Consultations,
        onlyAuthorized: true,
        notExact: true,
        withNavigation: defaultNavigationState,
      },
      [RouteEnum.SERVICE_PROVIDERS]: {
        title: t('routes_nav_service_providers'),
        path: '/service-providers',
        component: ServiceProvider,
        onlyAuthorized: true,
        withNavigation: defaultNavigationState,
      },
      [RouteEnum.BUDGET]: {
        title: t('routes_nav_budget'),
        path: '/budget',
        component: withContainer(BudgetRoutes),
        onlyAuthorized: true,
        notExact: true,
        withNavigation: defaultNavigationState,
        withBudget: true,
      },
      [RouteEnum.PROFILE]: {
        title: t('user_profile'),
        path: '/profile',
        component: withContainer(ProfileRoutes),
        onlyAuthorized: true,
        notExact: true,
        withNavigation: defaultNavigationState,
      },
      [RouteEnum.MARKETPLACE]: {
        title: t('routes_nav_marketplace'),
        path: '/marketplace',
        component: withContainer(MarketplaceRoutes),
        onlyAuthorized: true,
        withNavigation: defaultNavigationState,
      },
      [RouteEnum.REGISTER_EMAIL_REQUESTED]: {
        title: t('routes_nav_register'),
        path: '/register/email-requested',
        component: RegistrationEmailRequested,
        withNavigation: initialNavigationState,
      },
      [RouteEnum.REGISTER_WWT]: {
        title: t('routes_nav_register'),
        path: '/register/wwt',
        component: RegistrationWWT,
        withNavigation: initialNavigationState,
      },
      [RouteEnum.REGISTER_ISN]: {
        title: t('routes_nav_register'),
        path: '/register/isn',
        component: RegistrationISN,
        withNavigation: initialNavigationState,
      },
      [RouteEnum.REGISTER_COMPLETE]: {
        title: t('routes_nav_register'),
        path: '/register/:token',
        component: CompleteRegistration,
        withNavigation: initialNavigationState,
      },
      [RouteEnum.REGISTER_REQUEST_EMAIL]: {
        title: t('routes_nav_register'),
        path: '/register',
        component: RequestRegistrationEmail,
        withNavigation: initialNavigationState,
      },
      [RouteEnum.CONFIRM_EMAIL]: {
        title: t('routes_nav_confirm_email'),
        path: '/confirm-email',
        component: withContainer(ConfirmEmail),
        withNavigation: logoNavigationState,
      },
      [RouteEnum.LOGIN]: {
        title: t('routes_nav_login'),
        path: '/login',
        component: Login,
      },
      [RouteEnum.SIGNUP]: {
        title: t('routes_nav_sign_up'),
        path: '/signup',
        component: Signup,
        withNavigation: initialNavigationState,
      },
      [RouteEnum.ACCEPTED_AGREEMENT]: {
        //title: t('routes_partner_agreement_accepted'),
        title: NO_TITLE,
        path: '/partner-agreement-accepted',
        component: AcceptedAgreement,
      },
      [RouteEnum.REJECTED_AGREEMENT]: {
        title: t('routes_nav_partner_agreement_rejected'),
        path: '/partner-agreement-declined',
        component: RejectedAgreement,
      },
      [RouteEnum.ERROR_AGREEMENT]: {
        title: t('routes_nav_partner_agreement_error'),
        path: '/partner-agreement-error',
        component: PartnerAgreementErrorPage,
      },
      [RouteEnum.LOGOUT]: {
        title: t('user_logout'),
        path: '/logout',
        component: Logout,
        withNavigation: initialNavigationState,
      },
      [RouteEnum.CREATE_ACCOUNT]: {
        title: t('routes_nav_create_account'),
        path: '/create-account',
        component: withContainer(CreateAccount),
        notExact: true,
        withNavigation: logoNavigationState,
      },
      [RouteEnum.SELECT_JOURNEY]: {
        title: t('routes_nav_select_journey'),
        path: '/select-journey',
        component: withContainer(SelectJourney),
        notExact: true,
        withNavigation: logoNavigationState,
      },
      [RouteEnum.PERSONALIZE_JOURNEY]: {
        title: t('routes_nav_personalize_journey'),
        path: '/personalize-journey',
        component: withContainer(PersonalizeJourney),
        notExact: true,
        withNavigation: logoNavigationState,
      },
      [RouteEnum.SETUP_PROFILE]: {
        title: t('routes_nav_set_up_profile'),
        path: '/set-up-profile',
        component: withContainer(SetUpProfile),
        notExact: true,
        withNavigation: logoNavigationState,
      },
      [RouteEnum.MIGRATE_ACCOUNT]: {
        path: '/migrate-account',
        title: NO_TITLE,
        component: withContainer(MigrateAccount),
        notExact: true,
        withNavigation: logoNavigationState,
      },
      [RouteEnum.CHANGE_JOURNEY]: {
        title: t('routes_nav_change_journey'),
        path: '/change-journey',
        component: withContainer(ChangeJourney),
        withNavigation: logoNavigationState,
      },
      [RouteEnum.BLOCKED_ACCOUNT]: {
        path: '/blocked-account',
        title: NO_TITLE,
        component: withContainer(BlockedAccount),
        withNavigation: {hide: false, variant: 'blue'},
      },
      [RouteEnum.VALIDATE_TOKEN]: {
        title: t('routes_nav_validation'),
        path: '/validate-email/:token',
        component: withContainer(ValidateToken),
        withNavigation: logoNavigationState,
      },
      [RouteEnum.VALIDATE_PRIVATE_EMAIL]: {
        path: '/validate-private-email/:token',
        component: ValidatePrivateEmail,
        title: t('routes_nav_private_email_validation'),
        withNavigation: logoNavigationState,
      },
      [RouteEnum.RESET_PASSWORD]: {
        title: t('routes_nav_reset_password'),
        path: '/reset-password/:uid/:token',
        component: ResetPassword,
        withNavigation: initialNavigationState,
      },
      [RouteEnum.FORGET_PASSWORD]: {
        title: t('routes_nav_recover_password'),
        path: '/recover-password',
        component: ForgetPassword,
        withNavigation: initialNavigationState,
      },
      [RouteEnum.ERROR]: {
        title: t('routes_nav_error'),
        path: '/error',
        component: ErrorPage,
        withNavigation: logoNavigationState,
      },
      [RouteEnum.EMAIL_TOKEN_EXPIRED]: {
        title: t('routes_nav_token_expired'),
        path: '/email-token-expired',
        component: TokenExpired,
      },
      [RouteEnum.PASSWORD_TOKEN_ERROR]: {
        title: t('routes_nav_password_token_error'),
        path: '/password-token-error',
        component: PasswordTokenError,
        withNavigation: initialNavigationState,
      },
      [RouteEnum.CANNOT_SIGNUP]: {
        path: '/cannot-signup',
        title: NO_TITLE,
        component: CannotSignUpPage,
        withNavigation: initialNavigationState,
      },
      [RouteEnum.MEMBER_PROMOTION]: {
        title: t('routes_nav_promotion'),
        path: '/home/eligible',
        component: withContainer(MemberPromotion),
        onlyAuthorized: true,
        withNavigation: logoNavigationState,
      },
      [RouteEnum.ASPIRING_INFO]: {
        title: t('routes_nav_aspiring_member_information'),
        path: '/home/aspiring',
        component: withContainer(MemberAspiring),
        onlyAuthorized: true,
      },
      [RouteEnum.PA_REDIRECT]: {
        path: '/partner-agreement',
        component: withContainer(PartnerAgreement),
        title: NO_TITLE,
        onlyAuthorized: true,
        withNavigation: defaultNavigationState,
      },
      [RouteEnum.BENEFITS_GUIDE_REDIRECT]: {
        path: '/benefits-guide',
        component: BenefitsGuideRoute,
        title: NO_TITLE,
        onlyAuthorized: true,
      },
      [RouteEnum.EN_REDIRECT]: {
        path: '/en/*',
        title: NO_TITLE,
        notExact: true,
        render: ({match}) => {
          const normalizedPath = match.url.replace(/^\/en/, '')
          history.push({
            pathname: normalizedPath,
          })
          return null
        },
      },
      [RouteEnum.NOT_FOUND]: {
        title: NO_TITLE,
        path: '/404',
        component: NotFoundPage,
        withNavigation: logoNavigationState,
      },
      [RouteEnum.STRING_OPTIONS]: {
        title: t('routes_nav_testing_translations'),
        path: '/string-options',
        onlyAuthorized: true,
        component: withContainer(StringOptions),
        withNavigation: defaultNavigationState,
      },
      [RouteEnum.NON_EXISTENT]: {
        title: NO_TITLE,
        path: '/*',
        render: () => {
          history.push('/404')
          return null
        },
      },
    }),
    [t, history]
  )

  const getPath = useCallback(
    (route: RouteEnum) => routes[route].path,
    [routes]
  )

  const navbarItems: NavigationMenuItem[] = useMemo(() => {
    const currentProductLine = productLine || FERTILITY
    const navbar = getNavigationMenu(currentProductLine)

    return navbar
      .filter((item) => {
        if (
          contractType === 'lite' &&
          (item === RouteEnum.BUDGET || item === RouteEnum.CONSULTATIONS)
        ) {
          return false
        }
        if (item === RouteEnum.BUDGET && !hasBudget) {
          return false
        }
        return true
      })
      .map((item) => ({
        path: getPath(item),
        title: routes[item].title,
      }))
  }, [getPath, routes, hasBudget, contractType, productLine])

  const goTo = useCallback(
    (route: RouteEnum) => {
      startTransition(() => {
        history.push(getPath(route))
      })
    },
    [history, getPath]
  )

  return {
    routes,
    navbarItems,
    getPath,
    goTo,
  }
}

const Routes: FC = () => {
  const location = useLocation()
  const pathname = location.pathname ?? ''
  const {i18n} = useTranslation()
  const {
    data,
    loading: loadingMeQuery,
    networkStatus,
  } = useMeQuery({
    notifyOnNetworkStatusChange: false,
  })
  const {changeLanguage} = useChangeLanguage()
  const {productLine} = useProductLine()

  const user = useMeStore((s) => s.user)

  const hasStartedLegacyOnboarding = useMeStore(
    (s) => s.hasStartedLegacyOnboarding
  )

  const isSsoLogin = useMeStore((s) => s.isSsoLogin)

  const isLoggedIn = useMeStore((s) => s.isLoggedIn)

  const isAccountCreated = useMeStore((s) => s.isAccountCreated)

  const isAuthorized = useMeStore((s) => s.isAuthorized)

  const isEmailConfirmed = useMeStore((s) => s.isEmailConfirmed)

  const hasAcceptedTerms = useMeStore((s) => s.hasAcceptedTerms)

  const companyName = useMeStore((s) => s.companyName)

  const companyHasBudget = useMeStore((s) => s.companyHasBudget)

  const countryCode = useMeStore((s) => s.countryCode)

  const loading = useMeStore((s) => s.loading)

  const isInConservativeCountry = useMeStore((s) => s.isInConservativeCountry)

  const {getPath, routes} = useRoutes(companyHasBudget)

  const currentAppRoute = Object.values(routes).find((route) =>
    pathname.includes(route.path)
  )
  const isNavMenuHiddenOnCurrentRoute =
    currentAppRoute?.withNavigation?.hideMenu

  useEffect(() => {
    const resyncLanguageLibrary = async () => {
      // If we have a storage item user has interacted with the language selector.

      const uiLanguage = localStorage.getItem('ui_language')

      if (isLoggedIn) {
        // If we have a storage item use storage value.
        if (uiLanguage && i18n.language !== uiLanguage) {
          await changeLanguage(uiLanguage)
        }

        // If we don't have a storage item, we use the server value.
        if (!uiLanguage) {
          const serverLang = data?.me?.member?.language

          if (serverLang && i18n.language !== serverLang) {
            await changeLanguage(serverLang)
          }
        }
      } else {
        // If user is not logged in, we rely on the storage value.
        uiLanguage !== i18n.language && changeLanguage(uiLanguage || 'en', true)
      }
    }

    resyncLanguageLibrary()
    // Omit i18n.language from deps to avoid re-running when user just changed language: effect would read stale localStorage and call changeLanguage(oldValue), overwriting the new choice.
  }, [changeLanguage, isLoggedIn, i18n.language, data?.me?.member?.language])

  useScript(
    {
      id: 'heap',
      removeScriptWithId: 'heap_source',
      content: `
      try {
        ;(window.heap = window.heap || []),
        (heap.load = function (e, t) {
          ;(window.heap.appid = e), (window.heap.config = t = t || {})
          var r = document.createElement('script')
          r.setAttribute('id', 'heap_source')
          ;(r.type = 'text/javascript'), (r.async = !0), (r.src = '${REACT_APP_BACKEND_URL}/js/heap-' + e + '.js')
          var a = document.getElementsByTagName('script')[0]
          if(!document.getElementById('heap_source')){
              a.parentNode.insertBefore(r, a)
              for (
                var n = function (e) {
                    return function () {
                         heap.push([e].concat(Array.prototype.slice.call(arguments, 0)))
                    }
                  },
                  p = [
                    'addEventProperties',
                    'addUserProperties',
                    'clearEventProperties',
                    'identify',
                    'resetIdentity',
                    'removeEventProperty',
                    'setEventProperties',
                    'track',
                    'unsetEventProperty',
                  ],
                  o = 0;
                o < p.length;
                o++
              )
                heap[p[o]] = n(p[o])
                }
            })
            if(${REACT_APP_HEAP_ANALYTICS_ID}){
              heap.load(${REACT_APP_HEAP_ANALYTICS_ID})
          }
    } catch (error) {
      console.error(error)
  }
`,
    },
    isLoggedIn &&
      hasAcceptedTerms &&
      // disable tracking for WWT in the UK
      !(companyName === 'World Wide Technology' && countryCode === 'GB')
  )

  if (loading || loadingMeQuery) {
    return <PageLoader position={'center'} />
  }

  const createAccountPath = getPath(RouteEnum.CREATE_ACCOUNT)
  const logoutPath = getPath(RouteEnum.LOGOUT)
  const isOnCreateAccountRoute =
    pathname === createAccountPath ||
    pathname.startsWith(`${createAccountPath}/`)
  const isOnLogoutRoute =
    pathname === logoutPath || pathname.startsWith(`${logoutPath}/`)

  const {
    completed: completedOnboardingPaths,
    resumePath: onBoardingUnfinished,
  } = getResumeRoute(user?.member, productLine, {
    isConservativeCountry: isInConservativeCountry(),
  })

  if (
    isLoggedIn &&
    isEmailConfirmed &&
    user?.member &&
    !hasAcceptedTerms &&
    !isOnCreateAccountRoute &&
    !isOnLogoutRoute
  ) {
    return <Redirect to={`${createAccountPath}/terms`} />
  }

  const isOnResumeOnboardingPath =
    !!onBoardingUnfinished &&
    (pathname === onBoardingUnfinished ||
      pathname.startsWith(`${onBoardingUnfinished}/`))

  const isOnCompletedOnboardingPath = completedOnboardingPaths.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  )

  const shouldForceOnboardingResume =
    Boolean(onBoardingUnfinished) &&
    !isOnResumeOnboardingPath &&
    !isOnCompletedOnboardingPath &&
    !isOnLogoutRoute

  if (shouldForceOnboardingResume) {
    return <Redirect to={onBoardingUnfinished!} />
  }

  // Check if we're currently refetching (networkStatus 4 = refetch)
  // This helps prevent redirects to login when data is being updated after mutations
  const isRefetching = loadingMeQuery || (loading && networkStatus === 4)

  const meContext = {
    hasStartedLegacyOnboarding,
    isSsoLogin,
    isAccountCreated,
    isAuthorized,
    isEmailConfirmed,
    isLoggedIn,
    companyHasBudget,
    email: user?.email || '',
    loading: loading || loadingMeQuery || isRefetching,
  }

  return (
    <Suspense
      fallback={
        <PageLoader
          /* For routes without a navigation menu show a centered page loader */
          position={isNavMenuHiddenOnCurrentRoute ? 'center' : 'top'}
        />
      }
    >
      <Switch>
        {/* Root redirects from / to the relevant path */}
        <Route
          exact
          path={'/'}
          render={() => {
            const {resumePath} = getResumeRoute(user?.member, productLine, {
              isConservativeCountry: isInConservativeCountry(),
            })

            if (meContext.isAuthorized) {
              return <Redirect to={`${getPath(RouteEnum.HOME)}`} />
            } else if (meContext.isAccountCreated) {
              if (meContext.hasStartedLegacyOnboarding) {
                return <Redirect to={`${getPath(RouteEnum.MIGRATE_ACCOUNT)}`} />
              } else {
                return (
                  <Redirect
                    to={resumePath ?? getPath(RouteEnum.SETUP_PROFILE)}
                  />
                )
              }
            } else if (meContext.isEmailConfirmed) {
              if (meContext.isSsoLogin) {
                return (
                  <Redirect
                    to={
                      resumePath ?? `${getPath(RouteEnum.CREATE_ACCOUNT)}/terms`
                    }
                  />
                )
              } else {
                return (
                  <Redirect
                    to={{
                      pathname:
                        resumePath ??
                        `${getPath(RouteEnum.CREATE_ACCOUNT)}/terms`,
                      state: {email: meContext.email},
                    }}
                  />
                )
              }
            } else if (meContext.isLoggedIn) {
              return <Redirect to={`${getPath(RouteEnum.CONFIRM_EMAIL)}`} />
            } else {
              return <Redirect to={`${getPath(RouteEnum.LOGIN)}`} />
            }
          }}
        />
        {/* Setup all the real routes */}
        {Object.values(routes)
          .filter((item) => {
            const currentProductLine = productLine || FERTILITY

            // Filter out STRINGS_TESTING route in production (dev only)
            if (item.path === '/strings-testing' && !isDevelopment) {
              return false
            }

            // Filter out budget routes if company doesn't have budget
            if (item.withBudget && !companyHasBudget) {
              return false
            }

            // Filter out marketplace route for pregnancy and menopause product lines
            if (item.path === '/marketplace') {
              if (
                currentProductLine === PREGNANCY_AND_POSTPARTUM ||
                currentProductLine === MENOPAUSE
              ) {
                return false
              }
            }

            // Filter out budget route for pregnancy and menopause product lines
            if (item.path === '/budget') {
              if (
                currentProductLine === PREGNANCY_AND_POSTPARTUM ||
                currentProductLine === MENOPAUSE
              ) {
                return false
              }
            }

            // Filter out service providers route for pregnancy product line
            if (item.path === '/service-providers') {
              if (currentProductLine === PREGNANCY_AND_POSTPARTUM) {
                return false
              }
            }

            return true
          })
          .map((routeSpec: RouteSpec) => (
            <AppRoute
              routeSpec={routeSpec}
              key={routeSpec.path}
              path={routeSpec.path}
              component={routeSpec.component}
              meContext={meContext}
            />
          ))}
      </Switch>
    </Suspense>
  )
}

export default Routes
